import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import hasDom from 'has-dom';

// utilities
import clsx from 'clsx';

/**
 * The SideBar component is a container for any content that will either render inline or off canvas based on an isDocked prop.
 * If isDocked prop is false, then the contents will render off canvas and be triggered from the show prop.
 * The off canvas functionality can be achieved from the right or left side of the screen.
 * */
function SideBar(props) {
  const {
    isDocked,
    children,
    className,
    fullWidth,
    header,
    show,
    onHide,
    position = 'left',
    uiContext,
    ...rest
  } = props;
  const scrollTopRef = useRef(0);
  const containerRef = useRef(null);
  useEffect(() => {
    containerRef.current = document.body;
  }, []);
  useEffect(() => {
    const openClassName = 'sidebar-open';
    if (hasDom() && containerRef.current && show) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const classNames = containerRef.current.className.split(' ') || [];
      if (!classNames.includes(openClassName)) {
        classNames.push(openClassName);
      }
      containerRef.current.className = classNames.join(' ');
      containerRef.current.style.top = `-${scrollTop}px`;
      scrollTopRef.current = scrollTop;
    }

    // Cleanup function to remove sidebar-open class when show becomes false or component unmounts
    return () => {
      if (containerRef.current) {
        const classNames = containerRef.current.className.split(' ') || [];
        const openIndex = classNames.indexOf(openClassName);
        if (openIndex !== -1) {
          classNames.splice(openIndex, 1);
          containerRef.current.className = classNames.join(' ');
          containerRef.current.style.top = null;
        }
        window.scrollTo(0, scrollTopRef.current);
      }
    };
  }, [show]);
  const handleDialogClick = e => {
    if (e.target !== e.currentTarget) {
      return;
    }
    onHide();
  };
  const wrapperClassNames = clsx(className, {
    'side-bar-container': true,
    floating: !isDocked,
    active: !isDocked && show
  });
  const sidebarWidth = fullWidth ? 12 : 6;
  const sidebarClassNames = clsx({
    ['col-xs-' + sidebarWidth]: !isDocked,
    'side-bar-wrapper': true,
    'sidebar-simple': uiContext === 'simple',
    [position]: !isDocked
  });
  const container = hasDom() && window.document.body;
  if (!show || !container) {
    return null;
  }
  return /*#__PURE__*/ReactDOM.createPortal(/*#__PURE__*/React.createElement("div", Object.assign({}, rest, {
    "data-cmp": "sideBar",
    className: wrapperClassNames,
    onClick: handleDialogClick,
    role: "button",
    tabIndex: "0"
  }), /*#__PURE__*/_jsx("div", {
    className: sidebarClassNames
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "side-bar-header"
  }, void 0, header), /*#__PURE__*/_jsx("div", {
    "data-cmp": "sideBar-content",
    className: "side-bar-content padding-horizontal-0"
  }, void 0, children))), container);
}
export default SideBar;